import { Helmet } from 'react-helmet-async';
import { Phone, AlertTriangle, DollarSign, Clock, Shield, CheckCircle2, Sun, Droplets, Wind } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TileRoofs = () => {
  // Schema for SEO
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Tile Roof Coating",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Savage Rejuvenation Coatings",
      "telephone": "+1-321-804-4413"
    },
    "areaServed": "Central Florida",
    "description": "Professional tile roof coating and sealing services in Orlando, Tampa, and Central Florida. Waterproof, protect, and restore clay and concrete tile roofs."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://savagerejuvenationcoatings.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Tile Roofs",
        "item": "https://savagerejuvenationcoatings.com/roofs/tile"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Tile Roof Problems & Coating Solutions | Orlando Tampa FL | Savage Rejuvenation</title>
        <meta name="description" content="Understand tile roof vulnerabilities in Florida's harsh climate. Get repair costs, damage statistics, and discover how sealing can protect your tile roof. Call (321) 804-4413." />
        <meta name="keywords" content="tile roof problems Florida, clay tile roof repair Orlando, concrete tile roof coating Tampa, tile roof sealing, Florida tile roof protection" />
        
        <link rel="canonical" href="https://savagerejuvenationcoatings.com/roofs/tile" />
        
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-secondary border-b-4 border-primary">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 flex items-center justify-between">
            <nav className="hidden lg:flex items-center gap-6">
              <a href="/" className="text-white hover:text-primary font-manrope font-semibold">Home</a>
              <a href="/reviews" className="text-white hover:text-primary font-manrope font-semibold">Reviews</a>
              <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold">Q&A</a>
              <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold">Blog</a>
            </nav>
            <div className="hidden lg:flex items-center gap-6">
              <a href="tel:3218044413" className="text-white hover:text-primary transition-colors flex items-center gap-2 text-lg font-manrope font-semibold">
                <Phone className="h-5 w-5" />
                (321) 804-4413
              </a>
            </div>
            <div className="lg:hidden">
              <a href="tel:3218044413">
                <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[2px_2px_0px_0px_#000] rounded-sm px-3 py-2">
                  <Phone className="h-5 w-5" />
                </Button>
              </a>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section 
          className="relative min-h-[500px] flex items-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1645892399945-bad477acd3e3?crop=entropy&cs=srgb&fm=jpg&q=85')`
          }}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-white">
            <h1 className="font-barlow text-4xl md:text-6xl font-bold uppercase mb-6">
              Tile Roofs
            </h1>
            <p className="text-xl md:text-2xl font-manrope max-w-3xl mb-8">
              Beautiful and iconic Florida roofing—but surprisingly vulnerable. 
              Learn the hidden costs and how to protect your tile investment.
            </p>
            <a href="tel:3218044413">
              <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000] rounded-sm text-lg px-8 py-6 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-5 w-5" />
                Get FREE Inspection
              </Button>
            </a>
          </div>
        </section>

        {/* Overview Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-8 text-center">
              About Tile Roofs
            </h2>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="font-manrope text-lg text-gray-700 mb-6">
                  Tile roofing—both clay and concrete—is a hallmark of Florida architecture. With proper care, 
                  tile roofs can last <strong className="text-primary">50-100+ years</strong>, making them one 
                  of the most durable roofing options available.
                </p>
                <p className="font-manrope text-lg text-gray-700 mb-6">
                  However, while the tiles themselves are extremely durable, the underlayment beneath them 
                  and the tiles' porous surface create vulnerabilities that many homeowners don't anticipate 
                  until costly damage occurs.
                </p>
                <div className="bg-accent-light border-2 border-black p-6">
                  <h3 className="font-barlow text-xl font-bold uppercase mb-3">Quick Facts</h3>
                  <ul className="space-y-2 font-manrope">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Tile lifespan: 50-100+ years</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Underlayment lifespan: Only 20-30 years</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Types: Clay, concrete, and slate</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <span>Porous surface absorbs water without sealing</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1645892399945-bad477acd3e3?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Tile roof in Florida"
                  className="w-full border-4 border-primary shadow-[8px_8px_0px_0px_#000]"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Problems Section */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                <span className="text-red-600">Weather Vulnerabilities</span> in Florida
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Don't be fooled by tile's durability—Florida's climate creates specific problems:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Droplets className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Water Absorption</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  Unsealed tiles are surprisingly porous:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Concrete tiles absorb up to 13% of their weight in water</li>
                  <li>• Moisture leads to mold and algae growth</li>
                  <li>• Freeze-thaw cycles cause cracking (rare but possible in FL)</li>
                  <li>• Weight increase stresses roof structure</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Wind className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Hurricane Damage</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  Tiles are heavy but not wind-proof:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Individual tiles can lift and break in high winds</li>
                  <li>• Debris impact cracks or shatters tiles</li>
                  <li>• Tile replacement costs $25-50+ per tile</li>
                  <li>• Broken tiles expose underlayment to water</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-red-300 shadow-[4px_4px_0px_0px_#DC2626] p-6">
                <div className="bg-red-100 w-16 h-16 rounded-sm flex items-center justify-center mb-4">
                  <Sun className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="font-barlow text-xl font-bold uppercase mb-3">Underlayment Failure</h3>
                <p className="font-manrope text-gray-700 mb-4">
                  The hidden problem beneath beautiful tiles:
                </p>
                <ul className="space-y-2 font-manrope text-gray-600">
                  <li>• Underlayment only lasts 20-30 years</li>
                  <li>• Tiles must be removed to replace it</li>
                  <li>• Costs $15,000-$30,000+ for underlayment replacement</li>
                  <li>• Many tile roofs need this by age 25</li>
                </ul>
              </div>
            </div>

            <div className="mt-12 bg-red-600 text-white p-8 border-2 border-black shadow-[6px_6px_0px_0px_#000]">
              <div className="flex items-center gap-4 mb-4">
                <AlertTriangle className="h-10 w-10" />
                <h3 className="font-barlow text-2xl font-bold uppercase">Damage Statistics in Florida</h3>
              </div>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-5xl font-barlow font-bold">45%</p>
                  <p className="font-manrope">of tile roofs over 20 years need underlayment work</p>
                </div>
                <div>
                  <p className="text-5xl font-barlow font-bold">$50+</p>
                  <p className="font-manrope">per tile for replacement (including labor)</p>
                </div>
                <div>
                  <p className="text-5xl font-barlow font-bold">3x</p>
                  <p className="font-manrope">more algae growth on unsealed tiles vs. sealed</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                <DollarSign className="inline h-10 w-10 text-primary" /> Repair & Replacement Costs
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Understanding the true cost of tile roof maintenance in Central Florida
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 border-2 border-black shadow-[4px_4px_0px_0px_#000] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-red-600">
                  Repair Costs
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Single tile replacement</span>
                    <span className="font-barlow font-bold text-xl">$25 - $75</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Multiple tile replacement (10+)</span>
                    <span className="font-barlow font-bold text-xl">$350 - $1,500</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Leak repair</span>
                    <span className="font-barlow font-bold text-xl">$400 - $1,200</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Flashing repair</span>
                    <span className="font-barlow font-bold text-xl">$300 - $800</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Storm damage repair (major)</span>
                    <span className="font-barlow font-bold text-xl">$2,000 - $10,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope font-bold">Professional cleaning (algae/mold)</span>
                    <span className="font-barlow font-bold text-xl">$400 - $1,000</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border-2 border-black shadow-[4px_4px_0px_0px_#000] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-red-600">
                  Replacement Costs
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Concrete tile (per sq ft installed)</span>
                    <span className="font-barlow font-bold text-xl">$10 - $18</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Clay tile (per sq ft installed)</span>
                    <span className="font-barlow font-bold text-xl">$15 - $30</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Underlayment replacement only</span>
                    <span className="font-barlow font-bold text-xl">$15,000 - $30,000</span>
                  </div>
                  <div className="flex justify-between items-center border-b pb-3">
                    <span className="font-manrope">Full tile roof (2,000 sq ft)</span>
                    <span className="font-barlow font-bold text-xl">$25,000 - $50,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Tile removal & disposal</span>
                    <span className="font-barlow font-bold text-xl">$2,000 - $5,000</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-red-100 border-2 border-red-300">
                  <p className="font-manrope text-red-800">
                    <strong>Florida Average:</strong> $25,000 - $55,000 for full tile roof replacement
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16 bg-accent-light">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                The <span className="text-primary">Coating Solution</span>
              </h2>
              <p className="font-manrope text-lg text-gray-700 max-w-3xl mx-auto">
                Professional tile roof sealing and coating protects your investment, prevents water absorption, 
                and keeps your roof looking beautiful for decades longer.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Droplets className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Waterproof Sealing</h3>
                    <p className="font-manrope text-gray-700">Our coating penetrates tile pores and creates a waterproof barrier, preventing moisture absorption entirely.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Algae & Mold Prevention</h3>
                    <p className="font-manrope text-gray-700">Sealed tiles resist algae, mold, and mildew growth—keeping your roof clean and attractive.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">Extend Underlayment Life</h3>
                    <p className="font-manrope text-gray-700">By preventing water penetration, coating extends underlayment life by 10+ years, delaying costly replacement.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-primary w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-barlow text-xl font-bold uppercase mb-1">75% Cost Savings</h3>
                    <p className="font-manrope text-gray-700">Sealing costs $3-6 per sq ft vs. $20+ for replacement. Protect now, save thousands later!</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border-2 border-black shadow-[6px_6px_0px_0px_#FF8C00] p-8">
                <h3 className="font-barlow text-2xl font-bold uppercase mb-6 text-center">
                  Sealing vs. Replacement
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Average sealing cost (2,000 sq ft)</span>
                    <span className="font-barlow font-bold text-xl text-green-600">$6,000 - $12,000</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-manrope">Average replacement cost</span>
                    <span className="font-barlow font-bold text-xl text-red-600">$25,000 - $55,000</span>
                  </div>
                  <div className="border-t-2 border-primary pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="font-manrope font-bold text-lg">Your Savings</span>
                      <span className="font-barlow font-bold text-2xl text-primary">$19,000 - $43,000</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-secondary text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase mb-6">
              Protect Your Tile Roof Investment
            </h2>
            <p className="text-xl font-manrope mb-8">
              Get a FREE inspection and quote. We'll assess your tile roof's condition, check for underlying 
              issues, and provide honest recommendations.
            </p>
            <a href="tel:3218044413">
              <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-white shadow-[4px_4px_0px_0px_#fff] rounded-sm text-xl px-10 py-7 font-barlow uppercase font-bold">
                <Phone className="mr-2 h-6 w-6" />
                Call Now: (321) 804-4413
              </Button>
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white py-8">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <p className="font-manrope text-gray-400">
              © 2026 Savage Rejuvenation Coatings. All Rights Reserved. | Serving Orlando, Tampa & Central Florida
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default TileRoofs;
